import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { SVG_COMPONENTS } from '@/assets/admin';
import { PageHeader } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

interface DesktopPageHeaderProps {
  title: string;
  subtitle?: string;
  showAddButton?: boolean;
  showExportButton?: boolean;
  onAddPress?: () => void;
  onExportPress?: () => void;
  addButtonText?: string;
  exportButtonText?: string;
  /** testID forwarded to the primary add button (default: none). */
  addButtonTestID?: string;
  /** accessibilityLabel forwarded to the primary add button (default: none). */
  addButtonAccessibilityLabel?: string;
  children?: React.ReactNode; // For custom actions
}

export const DesktopPageHeader: React.FC<DesktopPageHeaderProps> = ({
  title,
  subtitle,
  showAddButton = false,
  showExportButton = false,
  onAddPress,
  onExportPress,
  addButtonText = 'Add New',
  exportButtonText = 'Export',
  addButtonTestID,
  addButtonAccessibilityLabel,
  children,
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();

  // Drop raw text nodes (including inside Fragments) from the actions slot.
  const renderSafeChildren = (nodes: React.ReactNode): React.ReactNode[] => {
    const array = React.Children.toArray(nodes);
    const safe: React.ReactNode[] = [];
    array.forEach((node) => {
      if (typeof node === 'string' || typeof node === 'number') return;
      if (React.isValidElement(node) && node.type === React.Fragment) {
        safe.push(...renderSafeChildren((node as any).props?.children));
        return;
      }
      safe.push(node);
    });
    return safe;
  };

  const rightAction = (
    <View style={styles.actionsSection}>
      {renderSafeChildren(children)}

      {showExportButton && (
        <Hoverable hover="row" style={styles.secondaryButton} onPress={onExportPress} testID="page-export">
          <SVG_COMPONENTS.DOWNLOAD width={16} height={16} color={admin.dataTable.text} />
          <Text style={styles.secondaryButtonText}>{exportButtonText}</Text>
        </Hoverable>
      )}

      {showAddButton && (
        <Hoverable
          hover="button"
          style={styles.primaryButton}
          onPress={onAddPress}
          testID={addButtonTestID}
          accessibilityRole="button"
          accessibilityLabel={addButtonAccessibilityLabel}
        >
          <Text style={styles.primaryButtonText}>{addButtonText}</Text>
        </Hoverable>
      )}
    </View>
  );

  return (
    <View style={styles.wrapper}>
      <PageHeader title={title} subtitle={subtitle} rightAction={rightAction} />
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      wrapper: {
        paddingBottom: 4,
      },
      actionsSection: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
      },
      primaryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        gap: 8,
        backgroundColor: admin.formInput.borderFocused,
      },
      primaryButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
      },
      secondaryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        gap: 8,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: admin.dataTable.border,
      },
      secondaryButtonText: {
        color: admin.dataTable.text,
        fontSize: 14,
        fontWeight: '600',
      },
    });
  });
